import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { settingsApi, type ConfirmationTemplateSettings } from "@/services/settings";
import { useCan } from "@/auth/permissions";
import { PageShell, LoadingState, ErrorState } from "@/shared/ui/PageHeader";
import { SettingsTabs } from "./SettingsTabs";

const inp =
  "w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] focus:border-blue-500 focus:outline-none";

/** ORDSTAT Part C3 · 出团确认单模板编辑（条款增删 + 售后块 + 页尾 · 非富文本 · 风格对齐设置页）。 */
export function ConfirmationTemplateSettingsPage() {
  const qc = useQueryClient();
  const canEdit = useCan("notifications.templates.edit");

  const q = useQuery({
    queryKey: ["settings", "confirmation-template"],
    queryFn: settingsApi.getConfirmationTemplate,
  });
  const [form, setForm] = useState<ConfirmationTemplateSettings | null>(null);
  useEffect(() => { if (q.data && !form) setForm(q.data); }, [q.data, form]);

  const save = useMutation({
    mutationFn: (p: ConfirmationTemplateSettings) => settingsApi.putConfirmationTemplate(p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "confirmation-template"] }),
    onError: (e) => alert((e as Error)?.message || "保存失败，请重试"),
  });

  if (q.isError) return <ErrorState message="确认单模板加载失败" onRetry={() => q.refetch()} />;
  if (!form) return <LoadingState />;

  const as = form.after_sales_block;
  const setAs = (patch: Partial<typeof as>) => setForm({ ...form, after_sales_block: { ...as, ...patch } });
  const setTerm = (i: number, patch: Partial<{ zh: string; en: string }>) =>
    setForm({ ...form, terms: form.terms.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const addTerm = () => setForm({ ...form, terms: [...form.terms, { zh: "", en: "" }] });
  const delTerm = (i: number) => setForm({ ...form, terms: form.terms.filter((_, j) => j !== i) });

  return (
    <PageShell title="出团确认单模板" subtitle="条款 / 售后专员联系块 / 页尾 · 出确认单时套用（对外文书）">
      <SettingsTabs />

      {/* 集合时间固定文案 + 页尾 */}
      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-[14px] font-bold text-slate-900">基础文案</h2>
        <label className="flex flex-col gap-1 text-[12px] font-medium text-slate-600">
          集合时间文案（Pick Up Time · 固定提示）
          <input className={inp} value={form.pickup_time_note} disabled={!canEdit}
            onChange={(e) => setForm({ ...form, pickup_time_note: e.target.value })} />
        </label>
        <label className="flex flex-col gap-1 text-[12px] font-medium text-slate-600">
          页尾客服电话
          <input className={inp} value={form.footer_phone} disabled={!canEdit}
            onChange={(e) => setForm({ ...form, footer_phone: e.target.value })} />
        </label>
      </section>

      {/* 售后专员联系块 */}
      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-[14px] font-bold text-slate-900">售后专员联系方式</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="微信 WeChat" value={as.wechat} disabled={!canEdit} onChange={(v) => setAs({ wechat: v })} />
          <Field label="国内电话 CN" value={as.cn_phone} disabled={!canEdit} onChange={(v) => setAs({ cn_phone: v })} />
          <Field label="美国电话 US" value={as.us_phone} disabled={!canEdit} onChange={(v) => setAs({ us_phone: v })} />
          <Field label="WhatsApp" value={as.whatsapp} disabled={!canEdit} onChange={(v) => setAs({ whatsapp: v })} />
          <Field label="Email" value={as.email} disabled={!canEdit} onChange={(v) => setAs({ email: v })} />
        </div>
      </section>

      {/* 条款（中英对照·增删）*/}
      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-[14px] font-bold text-slate-900">注意事项与条款（中英对照 · {form.terms.length} 条）</h2>
          {canEdit && (
            <button onClick={addTerm} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:bg-slate-50">
              <Plus size={13} /> 加一条
            </button>
          )}
        </div>
        <div className="flex flex-col gap-3">
          {form.terms.map((t, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50/50 p-3">
              <span className="mt-2 w-5 shrink-0 text-center text-[12px] font-semibold text-slate-500">{i + 1}</span>
              <div className="flex flex-1 flex-col gap-2">
                <textarea className={`${inp} h-auto`} rows={2} placeholder="中文条款" value={t.zh} disabled={!canEdit}
                  onChange={(e) => setTerm(i, { zh: e.target.value })} />
                <textarea className={`${inp} h-auto`} rows={2} placeholder="English term" value={t.en} disabled={!canEdit}
                  onChange={(e) => setTerm(i, { en: e.target.value })} />
              </div>
              {canEdit && (
                <button onClick={() => delTerm(i)} title="删除本条"
                  className="mt-1 shrink-0 rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
          {form.terms.length === 0 && <p className="py-4 text-center text-[12px] text-slate-400">暂无条款，点「加一条」添加。</p>}
        </div>
      </section>

      {canEdit && (
        <div className="flex items-center gap-3">
          <button onClick={() => save.mutate(form)} disabled={save.isPending}
            className="h-9 rounded-md bg-blue-600 px-5 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {save.isPending ? "保存中…" : "保存模板"}
          </button>
          {save.isSuccess && <span className="text-[12px] text-emerald-600">✓ 已保存</span>}
        </div>
      )}
    </PageShell>
  );
}

function Field({ label, value, onChange, disabled }: { label: string; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <label className="flex flex-col gap-1 text-[12px] font-medium text-slate-600">
      {label}
      <input className={inp} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export default ConfirmationTemplateSettingsPage;
